import React, {
  RefObject,
  FC,
} from 'react';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import fromPairs from 'lodash/fromPairs';
import {
  formatDateOnly,
} from 'utils/date';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import PatientPaymentsOverview from 'pages/Dashboard/pages/Billing/Payments/PatientPaymentsOverview';
import EOBsOverview from 'pages/Dashboard/pages/Billing/Payments/EOBsOverview';
import AddIcon from '@mui/icons-material/Add';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import {
  PaymentsEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import SearchIcon from 'core/icons/search';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useTableReset,
} from 'core/hooks';
import {
  EOBFilterDTO,
  PatientPaymentTransactionFilterDTO,
} from 'dtos';
import Filters from 'core/Filters';
import {
  usePaymentFilter,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  FilterInstance,
} from 'core/Filters/types';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import {
  DateRange,
} from 'types/dateRange';
import WithdrawIcon from 'core/icons/withdraw';

const paymentTypes = ['eobs', 'patient-payments'] as const;
export type PaymentFilterQuery = PatientPaymentTransactionFilterDTO & EOBFilterDTO;
export type OverviewProps = {
  dateRange: DateRange;
  searchQuery?: string;
  filterQuery?: PaymentFilterQuery;
  tableInstanceRef?: RefObject<VirtualTableInstance>;
};
export type ViewName = typeof paymentTypes[number];

type View = {
  Component: FC<OverviewProps>;
  searchPlaceholder: string;
};

const paymentTabs = [{
  label: 'EOBs',
  key: 'eobs',
}, {
  label: 'Patient Payments',
  key: 'patient-payments',
}];

const indexesByType: Readonly<Partial<Record<ViewName, number>>> = Object.freeze(
  fromPairs(paymentTypes.map((key, index) => [key, index])),
);

const viewsByType: Record<ViewName, View> = {
  'patient-payments': {
    Component: PatientPaymentsOverview,
    searchPlaceholder: 'Enter patient name, phone or date of birth',
  },
  eobs: {
    Component: EOBsOverview,
    searchPlaceholder: 'Enter EOB/Payment number or Payer name',
  },
};

const changeSubscription = {
  channel: paymentsEventChannel,
  event: 'onPaymentsDataChanged' as keyof PaymentsEventChannel,
};

export default function Payments() {
  const { reset, tableInstanceRef } = useTableReset<PaymentsEventChannel>({ changeSubscription });
  const { type = 'eobs' } = useParams<{ type?: ViewName }>();
  const navigate = useNavigate();
  const isEOBs = type === 'eobs';

  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
    dateRange,
    setDateRange,
  } = usePaymentFilter(type);

  const onFilterUpdate = () => {
    setFilterQuery(filterInstanceRef?.current?.getFilterQuery?.() ?? {});
    reset();
  };
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();

  const {
    Component: Overview,
    searchPlaceholder: placeholderText,
  } = viewsByType[type];

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-4">
      <div className="min-w-0 w-full shrink-0 overflow-x-auto px-4 py-2 -my-2">
        <div className="flex w-full min-w-max items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <NavigationTabs
              classes={{
                root: '!min-h-8 !h-8 [&_.MuiTabs-scroller]:!h-8 [&_.MuiTabs-flexContainer]:!h-8 !items-center',
                tab: '!font-semibold hover:!text-blue-500',
                active: '!text-blue-500 !border-blue-500',
              }}
              tabs={paymentTabs}
              activeIndex={indexesByType[type] ?? 0}
              onClick={(index) => {
                if (type !== paymentTypes[index]) {
                  const dateRangeParams = `${formatDateOnly(dateRange.min)}--${formatDateOnly(dateRange.max)}`;
                  navigate(`/billing/payments/${paymentTypes[index]}?dates=${dateRangeParams}`);
                  setSearchQuery('');
                  reset();
                }
              }}
            />
            <SearchInput
              key={`${type}-search-input`}
              placeholder={placeholderText}
              className="w-96"
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                reset();
              }}
              inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
              icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
              id="patient-search"
            />
            <DateRangePicker
              value={dateRange}
              onChange={(nextDateRange) => {
                if (nextDateRange) {
                  setDateRange(nextDateRange);
                  reset();
                }
              }}
            />
            <Filters
              key={`${type}-filters`}
              instanceRef={filterInstanceRef as RefObject<FilterInstance>}
              onFilterUpdate={onFilterUpdate}
              pageObject={isEOBs ? 'EOBs' : 'patientPaymentsTransactions'}
              useStorage
              dateRangeProps={isEOBs
                ? { PaymentDateRange: { maxDate: null } }
                : undefined}
            />
          </div>
          {type === 'eobs' && (
            <div className="flex items-center gap-2">
              <Button
                className="!h-8"
                variant="outlined"
                type="button"
                color="primary"
                startIcon={<DocumentScannerOutlinedIcon className="!w-4 !h-4 !text-inherit" />}
                onClick={() => { paymentsEventChannel.emit('onParseEOBScanOpen'); }}
              >
                Scan EOB
              </Button>
              <Button
                className="!h-8"
                variant="contained"
                type="button"
                color="primary"
                startIcon={<AddIcon className="!w-4 !h-4" />}
                onClick={() => { paymentsEventChannel.emit('onEOBDialogOpen', {}); }}
              >
                Add EOB
              </Button>
            </div>
          )}
          {type === 'patient-payments' && (
            <Button
              className="!h-8"
              variant="contained"
              color="primary"
              startIcon={<WithdrawIcon className="!w-4 !h-4" />}
              onClick={() => {
                paymentsEventChannel.emit('onAdjustPayment', {
                  view: {
                    type: 'refund',
                    hideNavigation: true,
                    defaultTitle: 'Refund Payment',
                  },
                });
              }}
            >
              Refund Payment
            </Button>
          )}
        </div>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Overview
          dateRange={dateRange}
          searchQuery={searchQuery.trim()}
          filterQuery={filterQuery}
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
        />
      </div>
    </div>
  );
}
